import React, { useEffect, useState } from "react"
import { getAPIBaseURL } from "../services/helpers"
import { getData } from "../services/request"

const HospitalWallet = () => {
  const [balance, setBalance] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [typeFilter, setTypeFilter] = useState("ALL")

  const fetchBalance = async () => {
    const url = `${getAPIBaseURL()}/v1/wallet/balance`
    const res = await getData(url, {}, {})
    if (res.error == "") {
      if (res.result.status === 200) {
        setBalance(res.result.data?.data?.balance || 0)
      } else {
        setError(res.result.data?.message || 'Failed to load balance')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const fetchTransactions = async () => {
    setLoading(true)
    const url = `${getAPIBaseURL()}/v1/wallet/transactions`
    const params = { limit: 100, skip: 0 }
    if (typeFilter && typeFilter !== 'ALL') {
      params.type = typeFilter
    }
    const res = await getData(url, {}, params)
    setLoading(false)
    if (res.error == "") {
      if (res.result.status === 200) {
        setTransactions(res.result.data?.data?.transactions || [])
        if (res.result.data?.data?.wallet?.balance !== undefined) {
          setBalance(res.result.data.data.wallet.balance)
        }
      } else {
        setError(res.result.data?.message || 'Failed to load transactions')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchBalance()
    fetchTransactions()
  }, [typeFilter])

  const formatCurrency = (amount, currency = 'NGN') => {
    const num = Number(amount)
    if (isNaN(num)) return amount
    return `${currency === 'NGN' ? '₦' : currency} ${num.toLocaleString()}`
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'deposit':
      case 'credit':
        return 'bg-green-100 text-green-700'
      case 'debit':
        return 'bg-red-100 text-red-700'
      case 'refund':
        return 'bg-blue-100 text-blue-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'failed':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const typeFilters = [
    { key: "ALL", label: "All" },
    { key: "deposit", label: "Deposits" },
    { key: "credit", label: "Credits" },
    { key: "refund", label: "Refunds" }
  ]

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-0">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wallet</h1>
        <p className="mt-1 text-sm text-gray-500">Manage your wallet balance and transactions</p>
      </div>

      {/* Balance Card */}
      <div className="mb-6 rounded-lg bg-black p-6 text-white shadow-lg">
        <div className="mb-2 text-sm font-medium opacity-90">Current Balance</div>
        <div className="text-4xl font-bold">
          {balance !== null ? formatCurrency(balance) : "Loading..."}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Type Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {typeFilters.map((filter) => (
          <button
            key={filter.key}
            onClick={() => setTypeFilter(filter.key)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
              typeFilter === filter.key
                ? 'border-black bg-black text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Transactions Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500">Loading transactions...</div>
        ) : transactions.length === 0 ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500">No transactions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Balance After</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Description</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((transaction) => (
                  <tr key={transaction._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getTypeColor(transaction.type)}`}>
                        {transaction.type?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-700">
                        {formatCurrency(transaction.balanceAfter, transaction.currency)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                        {transaction.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {transaction.description || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(transaction.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default HospitalWallet

